import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiJson } from '../api/client.js';
import { clientLabel } from '../utils/clientLabel.js';
import { FieldErrors } from '../components/FieldErrors.jsx';
import { ProjectTypeField, mergeTypeLists, projectTypeLabel } from '../components/ProjectTypeField.jsx';
import { Modal, ModalConfirmFooter } from '../components/Modal.jsx';
import { PageWelcome } from '../components/PageWelcome.jsx';
import { StatusBadge } from '../components/StatusBadge.jsx';
import { ProgressBar } from '../components/ProgressBar.jsx';
import { formatDate } from '../utils/formatDate.js';
import { statusLabel } from '../utils/statusUi.js';
import { SearchableSelect } from '../components/SearchableSelect.jsx';

const SORT_COLUMNS = [
  { key: 'client', label: 'Client / Project' },
  { key: 'status', label: 'Status' },
  { key: 'progress', label: 'Progress' },
  { key: 'due_date', label: 'Due Date' },
  { key: 'type', label: 'Type' },
];

const DEFAULT_SORT = { key: 'client', dir: 'asc' };

function compareProjects(a, b, key, dir, types) {
  const tie = Number(a.id) - Number(b.id);
  if (key === 'due_date') {
    const ad = a.due_date ? Date.parse(a.due_date) : NaN;
    const bd = b.due_date ? Date.parse(b.due_date) : NaN;
    const aMissing = Number.isNaN(ad);
    const bMissing = Number.isNaN(bd);
    if (aMissing || bMissing) {
      if (aMissing && bMissing) return tie;
      return aMissing ? 1 : -1;
    }
    const cmp = ad - bd;
    return cmp === 0 ? tie : dir === 'desc' ? -cmp : cmp;
  }

  let cmp = 0;
  if (key === 'client') {
    cmp = clientLabel(a.client).localeCompare(clientLabel(b.client), undefined, { sensitivity: 'base' });
  } else if (key === 'status') {
    cmp = statusLabel(a.status).localeCompare(statusLabel(b.status), undefined, { sensitivity: 'base' });
  } else if (key === 'progress') {
    cmp = (Number(a.progress_percent) || 0) - (Number(b.progress_percent) || 0);
  } else if (key === 'type') {
    cmp = projectTypeLabel(types, a.type).localeCompare(projectTypeLabel(types, b.type), undefined, { sensitivity: 'base' });
  }
  return cmp === 0 ? tie : dir === 'desc' ? -cmp : cmp;
}

export function AdminProjectsPage() {
  const [search] = useSearchParams();

  /** @type {[Array<any>, Function]} */
  const [projects, setProjects] = useState([]);
  /** @type {[Array<{ id: number, name: string }>, Function]} */
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientId, setClientId] = useState(() => search.get('client_id') || '');
  const [statusFilter, setStatusFilter] = useState(() => search.get('status') || '');
  const [name, setName] = useState('');
  const [type, setType] = useState('site');
  const [status, setStatus] = useState('draft');
  const [siteUrl, setSiteUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [templates, setTemplates] = useState([]);
  const [types, setTypes] = useState([]);
  const typeFieldRef = useRef(null);
  const [createStep, setCreateStep] = useState('start');
  /** @type {[number | null, Function]} */
  const [templateId, setTemplateId] = useState(null);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  /** @type {[{ id: number, name: string } | null, Function]} */
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [sort, setSort] = useState(DEFAULT_SORT);

  const toggleSort = (key) => {
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  };

  const closeCreate = () => {
    setShowCreate(false);
    setName('');
    setSiteUrl('');
    setNotes('');
    setType('site');
    setStatus('draft');
    setTemplateId(null);
    setCreateStep('start');
    setFieldErrors(undefined);
  };

  const openCreate = () => {
    setCreateStep('start');
    setTemplateId(null);
    setFieldErrors(undefined);
    setShowCreate(true);
  };

  const chooseCustom = () => {
    setTemplateId(null);
    setType('site');
    setStatus('draft');
    setNotes('');
    setCreateStep('details');
  };

  const chooseTemplate = (template) => {
    setTemplateId(template.id);
    setType(template.type || 'site');
    setStatus(template.status || 'draft');
    setNotes(template.notes || '');
    setCreateStep('details');
  };

  const loadProjects = () => {
    const q = clientId ? `?client_id=${encodeURIComponent(clientId)}` : '';
    return apiJson(`/api/admin/projects${q}`).then(setProjects);
  };

  useEffect(() => {
    const cid = search.get('client_id');
    if (cid) {
      setClientId(cid);
    }
    const status = search.get('status');
    if (status) {
      setStatusFilter(status);
    }
    if (search.get('create') === '1') {
      setCreateStep('start');
      setShowCreate(true);
    }
  }, [search]);

  useEffect(() => {
    setLoading(true);
    Promise.all([apiJson('/api/admin/clients'), loadProjects()])
      .then(([c]) => setClients(c))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [clientId]);

  useEffect(() => {
    apiJson('/api/admin/project-types')
      .then((incoming) => setTypes((current) => mergeTypeLists(current, incoming)))
      .catch(() => setTypes([]));
  }, []);

  useEffect(() => {
    if (!showCreate) return;
    apiJson('/api/admin/project-templates')
      .then(setTemplates)
      .catch(() => setTemplates([]));
    apiJson('/api/admin/project-types')
      .then((incoming) => setTypes((current) => mergeTypeLists(current, incoming)))
      .catch(() => setTypes([]));
  }, [showCreate]);

  const onCreate = async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors(undefined);
    setSubmitting(true);
    try {
      const resolvedType = await typeFieldRef.current.resolve();
      await apiJson('/api/admin/projects', {
        method: 'POST',
        body: {
          client_id: Number(clientId),
          template_id: templateId,
          name,
          type: resolvedType,
          status,
          site_url: siteUrl || null,
          notes: notes || null,
        },
      });
      closeCreate();
      setLoading(true);
      await loadProjects();
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setFieldErrors(err.body?.errors);
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async () => {
    if (!projectToDelete) {
      return;
    }
    setError('');
    setDeleting(true);
    try {
      await apiJson(`/api/admin/projects/${projectToDelete.id}`, { method: 'DELETE' });
      setProjectToDelete(null);
      setLoading(true);
      await loadProjects();
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setProjectToDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  const queryFilter = (search.get('q') || '').trim().toLowerCase();
  const visibleProjects = useMemo(() => {
    const filtered = projects.filter((p) => {
      if (statusFilter && p.status !== statusFilter) return false;
      if (!queryFilter) return true;
      const hay = `${p.name} ${p.client?.company_name || p.client?.name || ''} ${p.type || ''}`.toLowerCase();
      return hay.includes(queryFilter);
    });
    return [...filtered].sort((a, b) => compareProjects(a, b, sort.key, sort.dir, types));
  }, [projects, statusFilter, queryFilter, sort, types]);

  return (
    <div>
      <PageWelcome
        title="Projects"
        subtitle="At-a-glance status, progress, and the next action for each engagement."
        actions={
          <>
            <Link to="/admin/project-templates" className="btn-secondary">
              Templates
            </Link>
            <button type="button" className="btn-primary" onClick={openCreate} data-tour="projects-create">
              + New project
            </button>
          </>
        }
      />
      {loading ? <p className="muted">Loading…</p> : null}
      {error ? <p className="err">{error}</p> : null}

      <div className="panel" style={{ marginTop: '1rem' }}>
        <div className="row-actions" style={{ flexWrap: 'wrap', marginBottom: '1rem' }} data-tour="projects-filter">
          <label className="field" style={{ minWidth: '14rem', flex: 1 }}>
            <span>Filter by client</span>
            <SearchableSelect value={clientId} onChange={(ev) => setClientId(ev.target.value)}>
              <option value="">All clients</option>
              {clients.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {clientLabel(c)}
                </option>
              ))}
            </SearchableSelect>
          </label>
          <label className="field" style={{ minWidth: '12rem' }}>
            <span>Filter by status</span>
            <SearchableSelect value={statusFilter} onChange={(ev) => setStatusFilter(ev.target.value)}>
              <option value="">All statuses</option>
              <option value="draft">draft</option>
              <option value="active">active</option>
              <option value="on_hold">on_hold</option>
              <option value="done">done</option>
              <option value="cancelled">cancelled</option>
            </SearchableSelect>
          </label>
        </div>

      <div className="table-wrap" style={{ boxShadow: 'none', border: 0 }} data-tour="projects-list">
        <table className="data-table">
          <thead>
            <tr>
              {SORT_COLUMNS.map((col) => {
                const active = sort.key === col.key;
                const ariaSort = active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none';
                return (
                  <th key={col.key} aria-sort={ariaSort}>
                    <button
                      type="button"
                      className={`pm2-sort-btn${active ? ' is-active' : ''}`}
                      onClick={() => toggleSort(col.key)}
                      aria-label={col.key === 'client' ? 'Sort by client and company name' : `Sort by ${col.label}`}
                    >
                      {col.label}
                      <span className="pm2-sort-indicator" aria-hidden="true">
                        {active ? (sort.dir === 'asc' ? '↑' : '↓') : '↕'}
                      </span>
                    </button>
                  </th>
                );
              })}
              <th />
            </tr>
          </thead>
          <tbody>
            {visibleProjects.map((p) => (
              <tr key={p.id}>
                <td>
                  <Link to={`/admin/projects/${p.id}`}>
                    <strong>{clientLabel(p.client) || p.client_id}</strong>
                  </Link>
                  <div className="muted small">{p.name}</div>
                </td>
                <td>
                  <StatusBadge status={p.status} />
                </td>
                <td style={{ minWidth: '8rem' }}>
                  <ProgressBar value={p.progress_percent ?? 0} tone="purple" />
                </td>
                <td>{p.due_date ? formatDate(p.due_date) : '—'}</td>
                <td>{projectTypeLabel(types, p.type)}</td>
                <td className="row-actions">
                  <Link to={`/admin/projects/${p.id}`} className="btn-link">
                    Open
                  </Link>
                  <button
                    type="button"
                    className="btn-link"
                    onClick={() => setProjectToDelete(p)}
                    data-tour="projects-delete"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </div>

      <Modal
        open={showCreate}
        size="lg"
        title={createStep === 'start' ? 'New project' : templateId ? 'New project from template' : 'Custom project'}
        description={
          createStep === 'start'
            ? 'Start from a template for a common project type, or build a custom project when none apply.'
            : undefined
        }
        onClose={closeCreate}
      >
        {createStep === 'start' ? (
          <div className="stack">
            <div className="template-choice-grid">
              {templates.map((template) => (
                <button
                  key={template.id}
                  type="button"
                  className="template-choice"
                  onClick={() => chooseTemplate(template)}
                >
                  <strong>{template.name}</strong>
                  <p className="muted small" style={{ margin: '0.35rem 0 0' }}>
                    {template.description
                      || `${(template.stages || []).length ? `${template.stages.length} stages · ` : ''}${(template.tasks || []).length} starter tasks`}
                  </p>
                </button>
              ))}
              <button type="button" className="template-choice" onClick={chooseCustom}>
                <strong>Custom project</strong>
                <p className="muted small" style={{ margin: '0.35rem 0 0' }}>
                  Start blank when a template doesn’t fit.
                </p>
              </button>
            </div>
            <div className="row-actions">
              <Link to="/admin/project-templates" className="btn-link" onClick={closeCreate}>
                Manage templates
              </Link>
              <button type="button" className="btn-secondary" onClick={closeCreate}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={onCreate} className="stack">
            {templateId ? (
              <p className="muted small" style={{ margin: 0 }}>
                Using “{templates.find((template) => template.id === templateId)?.name}”. Starter tasks are added when
                the project is created.
              </p>
            ) : (
              <p className="muted small" style={{ margin: 0 }}>
                Custom project. Nothing is copied from a template.
              </p>
            )}
            <label className="field">
              <span>Client</span>
              <SearchableSelect value={clientId} onChange={(ev) => setClientId(ev.target.value)} required>
                <option value="" disabled>
                  Select client
                </option>
                {clients.map((c) => (
                  <option key={c.id} value={String(c.id)}>
                    {clientLabel(c)}
                  </option>
                ))}
              </SearchableSelect>
            </label>
            <label className="field">
              <span>Name</span>
              <input value={name} onChange={(ev) => setName(ev.target.value)} required />
            </label>
            <ProjectTypeField
              ref={typeFieldRef}
              value={type}
              onChange={setType}
              types={types}
              onTypesChange={setTypes}
            />
            <label className="field">
              <span>Status</span>
              <SearchableSelect value={status} onChange={(ev) => setStatus(ev.target.value)}>
                <option value="draft">draft</option>
                <option value="active">active</option>
                <option value="on_hold">on_hold</option>
                <option value="done">done</option>
                <option value="cancelled">cancelled</option>
              </SearchableSelect>
            </label>
            <label className="field">
              <span>Site URL (optional)</span>
              <input value={siteUrl} onChange={(ev) => setSiteUrl(ev.target.value)} placeholder="https://…" />
            </label>
            <label className="field">
              <span>Notes (optional)</span>
              <textarea rows={3} value={notes} onChange={(ev) => setNotes(ev.target.value)} />
            </label>
            <FieldErrors errors={fieldErrors} />
            <div className="row-actions" style={{ marginTop: '0.5rem' }}>
              <button type="button" className="btn-secondary" onClick={() => setCreateStep('start')} disabled={submitting}>
                Back
              </button>
              <button type="submit" className="btn-primary" disabled={submitting || !clientId}>
                {submitting ? 'Saving…' : 'Create project'}
              </button>
            </div>
          </form>
        )}
      </Modal>

      <Modal
        open={Boolean(projectToDelete)}
        title={projectToDelete ? `Delete “${projectToDelete.name}”?` : 'Delete project'}
        description="This permanently removes the project and its tasks, notes, media, and related data."
        onClose={() => {
          if (!deleting) {
            setProjectToDelete(null);
          }
        }}
        footer={
          <ModalConfirmFooter
            danger
            confirmLabel="Delete"
            confirmBusy={deleting}
            onCancel={() => setProjectToDelete(null)}
            onConfirm={() => void onDelete()}
          />
        }
      >
        <p className="muted small" style={{ margin: 0 }}>
          This action is permanent.
        </p>
      </Modal>
    </div>
  );
}
